import { Box, Button, chakra, HStack, Icon, RadioCard, Text, VStack } from '@chakra-ui/react';
import { MentionBoostPlacementMode } from '@fluenceur/types';
import { InfoCircle, NavArrowDown, NavArrowUp, ShieldCheck, WarningTriangle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { PlacementCampaignList } from './PlacementCampaignList';
import { PlacementConflictNotice } from './PlacementConflictNotice';
import { PlacementNamingPreview } from './PlacementNamingPreview';
import type { MentionBoostPlacementState } from './useMentionBoostPlacement';

interface PlacementFieldProps {
  placement: MentionBoostPlacementState;
  // The campaign name stored with the setup, used as the collapsed label before
  // the listing has resolved the campaign.
  placementCampaignName: string | null;
  productHandle: string | null;
  productTitle: string | null;
  creatorUsername: string;
}

/**
 * Where the boost is placed on Meta. Collapsed by default and set to the new
 * Fluenceur campaign: a brand that does not know Ads Manager never has to open
 * it, and the default parcours stays exactly as it was. Opening it offers the
 * opt-in mode, the brand's compatible campaigns, and the ownership rule that
 * governs everything Fluenceur does inside them.
 */
export function PlacementField({
  placement,
  placementCampaignName,
  productHandle,
  productTitle,
  creatorUsername,
}: PlacementFieldProps) {
  const { t } = useTranslation();

  const isExistingCampaign =
    placement.placementMode === MentionBoostPlacementMode.EXISTING_CAMPAIGN;
  const selectedName = placement.selectedCampaign?.name ?? placementCampaignName;
  const collapsedValue =
    isExistingCampaign && selectedName
      ? selectedName
      : t('mentions.boost.setup.placement.newCampaignValue');

  return (
    <Box border="1px solid" borderColor="gray.200" rounded="lg" px={3} py={2.5}>
      <chakra.button
        type="button"
        w="full"
        textAlign="left"
        onClick={placement.toggleExpanded}
        aria-expanded={placement.isExpanded}
      >
        <HStack justify="space-between" gap={3}>
          <VStack align="stretch" gap={0.5} minW={0}>
            <Text fontSize="2xs" color="fg.muted" textTransform="uppercase" letterSpacing="wider">
              {t('mentions.boost.setup.placement.title')}
            </Text>
            <Text fontSize="sm" fontWeight="medium" truncate>
              {collapsedValue}
            </Text>
          </VStack>
          <Icon size="sm" color="fg.muted" flexShrink={0}>
            {placement.isExpanded ? <NavArrowUp /> : <NavArrowDown />}
          </Icon>
        </HStack>
      </chakra.button>

      {placement.isExpanded && (
        <VStack align="stretch" gap={3} mt={3}>
          {placement.isUnavailable ? (
            <PlacementUnavailableNotice
              // A brand whose placement is already stored on a campaign must
              // never be locked in a mode whose selector is hidden: the way back
              // to the default placement stays reachable even here.
              onBackToNewCampaign={isExistingCampaign ? placement.resetToNewCampaign : null}
            />
          ) : (
            <>
              <RadioCard.Root
                colorPalette="primary"
                value={placement.placementMode}
                onValueChange={(details) => {
                  if (details.value) {
                    placement.selectMode(details.value as MentionBoostPlacementMode);
                  }
                }}
              >
                <VStack align="stretch" gap={2}>
                  <RadioCard.Item rounded="lg" value={MentionBoostPlacementMode.NEW_CAMPAIGN}>
                    <RadioCard.ItemHiddenInput />
                    <RadioCard.ItemControl>
                      <RadioCard.ItemContent>
                        <RadioCard.ItemText>
                          {t('mentions.boost.setup.placement.modeNewLabel')}
                        </RadioCard.ItemText>
                        <RadioCard.ItemDescription>
                          {t('mentions.boost.setup.placement.modeNewDescription')}
                        </RadioCard.ItemDescription>
                      </RadioCard.ItemContent>
                      <RadioCard.ItemIndicator />
                    </RadioCard.ItemControl>
                  </RadioCard.Item>

                  <RadioCard.Item rounded="lg" value={MentionBoostPlacementMode.EXISTING_CAMPAIGN}>
                    <RadioCard.ItemHiddenInput />
                    <RadioCard.ItemControl>
                      <RadioCard.ItemContent>
                        <RadioCard.ItemText>
                          {t('mentions.boost.setup.placement.modeExistingLabel')}
                        </RadioCard.ItemText>
                        <RadioCard.ItemDescription>
                          {t('mentions.boost.setup.placement.modeExistingDescription')}
                        </RadioCard.ItemDescription>
                      </RadioCard.ItemContent>
                      <RadioCard.ItemIndicator />
                    </RadioCard.ItemControl>
                  </RadioCard.Item>
                </VStack>
              </RadioCard.Root>

              {isExistingCampaign && (
                <PlacementCampaignList
                  campaigns={placement.campaigns}
                  isLoading={placement.isLoading}
                  hiddenCount={placement.hiddenCount}
                  isTruncated={placement.isTruncated}
                  search={placement.search}
                  hasSearch={placement.hasSearch}
                  selectedCampaignId={placement.placementCampaignId}
                  onSearchChange={placement.setSearch}
                  onClearSearch={placement.clearSearch}
                  onSelect={placement.selectCampaign}
                  onBackToNewCampaign={placement.resetToNewCampaign}
                />
              )}

              {isExistingCampaign && placement.isSelectionUnresolved && (
                <PlacementUnresolvedSelectionNotice
                  campaignName={selectedName}
                  onBackToNewCampaign={placement.resetToNewCampaign}
                />
              )}

              {isExistingCampaign && placement.conflictAdSetName && (
                <PlacementConflictNotice adSetName={placement.conflictAdSetName} />
              )}

              {/* Nothing will be created in a campaign that is no longer
                  selectable, so the naming preview would be a lie there. */}
              {isExistingCampaign && selectedName && !placement.isSelectionUnresolved && (
                <PlacementNamingPreview
                  campaignName={selectedName}
                  productHandle={productHandle}
                  productTitle={productTitle}
                  creatorUsername={creatorUsername}
                />
              )}
            </>
          )}

          {isExistingCampaign && !placement.isUnavailable && <PlacementOwnershipFooter />}
        </VStack>
      )}
    </Box>
  );
}

/**
 * The campaigns could not be read with the connected ad account. The mode is
 * simply not offered, with a plain explanation — never a raw provider error.
 */
function PlacementUnavailableNotice({
  onBackToNewCampaign,
}: {
  onBackToNewCampaign: (() => void) | null;
}) {
  const { t } = useTranslation();

  return (
    <VStack align="stretch" gap={2}>
      <HStack gap={1.5} align="flex-start">
        <Icon size="xs" color="fg.muted" mt={0.5}>
          <InfoCircle />
        </Icon>
        <Text fontSize="xs" color="fg.muted">
          {t('mentions.boost.setup.placement.unavailable')}
        </Text>
      </HStack>
      {onBackToNewCampaign && (
        <Button size="xs" variant="outline" rounded="full" onClick={onBackToNewCampaign}>
          {t('mentions.boost.setup.placement.emptyCta')}
        </Button>
      )}
    </VStack>
  );
}

/**
 * The stored campaign is not in the account's compatible list any more (deleted,
 * archived, or moved to another ad account). Launching would be refused, so the
 * brand is told here rather than at launch time.
 */
function PlacementUnresolvedSelectionNotice({
  campaignName,
  onBackToNewCampaign,
}: {
  campaignName: string | null;
  onBackToNewCampaign: () => void;
}) {
  const { t } = useTranslation();

  return (
    <VStack
      align="stretch"
      gap={2}
      border="1px solid"
      borderColor="yellow.emphasized"
      bg="yellow.subtle"
      rounded="lg"
      px={3}
      py={2.5}
    >
      <HStack align="flex-start" gap={2}>
        <Icon size="sm" color="yellow.fg" mt={0.5}>
          <WarningTriangle />
        </Icon>
        <Text fontSize="xs" color="yellow.fg">
          {t('mentions.boost.setup.placement.selectionUnresolved', {
            campaign: campaignName ?? '',
          })}
        </Text>
      </HStack>
      <Button size="xs" variant="outline" rounded="full" onClick={onBackToNewCampaign}>
        {t('mentions.boost.setup.placement.emptyCta')}
      </Button>
    </VStack>
  );
}

/** The non-interference rule, stated where the brand makes the choice. */
function PlacementOwnershipFooter() {
  const { t } = useTranslation();

  return (
    <HStack gap={1.5} align="flex-start">
      <Icon size="xs" color="fg.muted" mt={0.5}>
        <ShieldCheck />
      </Icon>
      <Text fontSize="xs" color="fg.muted">
        {t('mentions.boost.setup.placement.ownershipFooter')}
      </Text>
    </HStack>
  );
}
